import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { Card, Title } from '@iris/react-components';
import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { getScaledImageSource } from '@atc/images';

import {
    DisclaimerText,
    Image,
    Section,
    Text,
} from 'reaxl';
import {
    OwnerAddress,
    OwnerPhoneNumber,
    OwnerWebsiteCTA,
} from 'reaxl-molecules';

import { getDealerDetailUrl } from '@/utilities/ddpUrlBuilder';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import { KBBDealerRatings } from '@/components/RatingsReviews';
import OperatingHours from '@/components/vehicledetails/OperatingHours';
import OwnerContact from '@/components/vehicledetails/OwnerContact/OwnerContact';

/**
 * Responsive image size configurations for dealer/owner images across different breakpoints
 * @constant {Object}
 */
const ownerImageSizes = {
    xs: {
        width: 396,
        height: 244,
    },
    sm: {
        width: 353,
        height: 217,
    },
    md: {
        width: 481,
        height: 296,
    },
    lg: {
        width: 407,
        height: 251,
    },
};

/**
 * Returns the appropriate image source object based on the provided source and device type
 * @param {Object} source - Image source object containing src and other properties
 * @param {Object} device - Device information from useDevice hook
 * @returns {Object} Image source object with responsive sizing
 */
const getImageSource = (source, device) => {
    const { src } = source || {};
    return src ? {
        alt: 'Dealer Image',
        src: getScaledImageSource({
            src,
            bgColor: 'e6e6e6',
            ...ownerImageSizes[device.mediaType],
        }),
        ...ownerImageSizes[device.mediaType],
    } : {
        src: '/cm-api/content/static/img/placeholders/dealer_fallback.svg',
        'aria-hidden': 'true',
        ...ownerImageSizes[device.mediaType],
    };
};

/**
 * DealerImage component renders the dealer/owner image with responsive sizing and fallback
 * @param {Object} props.source - Image source object containing src and other properties
 * @param {Object} props.device - Device information from useDevice hook
 * @returns {React.ReactElement} Rendered Image component with responsive sizing
 */
function DealerImage({ source, device }) {
    const imgSrc = getImageSource(source, device);

    return (
        <Image
            key="dealer-img"
            className={clsx('img-responsive-scale img-rounded', { 'bg-neutral-tertiary': !imgSrc?.src, 'margin-bottom-5': device.mediaType === 'xs' })}
            isResponsive
            {...imgSrc}
        />
    );
}

DealerImage.propTypes = {
    /** Image source object containing src and other properties */
    source: PropTypes.object,
    /** Device information from useDevice hook */
    device: PropTypes.object,
};

/**
 * Generates the owner/dealer title element, optionally wrapping it in a link to the Dealer Detail Page (DDP)
 *
 * @param {boolean} isDdpLinkEnabled - Feature flag indicating if DDP links are enabled
 * @param {Object} owner - Owner/dealer information object
 * @param {string} owner.name - The dealer/owner name to display
 * @param {string} ddpBasePath - Base path for constructing DDP URLs
 * @returns {React.ReactElement|string} Either a linked dealer name or plain text name
 */
const getOwnerTitle = (isDdpLinkEnabled, owner, ddpBasePath) => {
    const href = isDdpLinkEnabled ? _get(getDealerDetailUrl(owner, ddpBasePath), 'href', '') : '';
    return href ? (
        <a
            data-cmp="ddpLink"
            className="text-inherit link-undecorated"
            href={href}
        >
            {owner.name}
        </a>
    ) : owner.name;
};

/**
 * OwnerDetails component displays comprehensive dealer/owner information on the VDP
 *
 * This component renders a detailed view of the vehicle's dealer/owner including:
 * - Dealer image with responsive sizing and fallback
 * - Dealer name (optionally linked to Dealer Detail Page)
 * - KBB dealer ratings and reviews
 * - Dealer license information
 * - Physical address (for non-virtual dealers)
 * - Phone number with click-to-call functionality
 * - Operating hours and language support
 * - Contact CTAs (email, chat, text, phone)
 * - Website CTA link
 *
 * The component uses feature flags to conditionally render various elements and
 * adapts its layout based on device breakpoints for optimal mobile/desktop experience.
 *
 * @param {Function} [props.phoneClickHandler] - Callback for phone CTA clicks, handles click-to-call functionality
 * @param {Function} [props.onEmailCTAClick] - Callback for email CTA clicks, typically opens contact forms
 * @param {Function} [props.chatClickHandler] - Callback for chat CTA clicks, initiates dealer chat sessions
 * @param {Function} [props.textClickHandler] - Callback for text/SMS CTA clicks, opens messaging applications
 * @returns {React.ReactElement} The rendered owner details section
 */
const OwnerDetails = memo(({
    disableContactCTAs = false,
    phoneClickHandler,
    uiContext,
    ...rest
}) => {

    const {
        call: [isCallEnabled],
        ddp_link: [isDdpLinkEnabled, { ddp_base_path: ddpBasePath }],
        dealer_rating: [isDealerRatingEnabled],
    } = useFeatures(['call', 'ddp_link', 'dealer_rating']);

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isLessThanMd = _get(device, 'lessThan.md', false);
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { id: inventoryId, owner = {}, marketExtension = {}, ecommWebsite = {} } = listing || {};
    const { id: ownerId, hours, isDealerOpen, images, license, location, phone, rating, languages, website = {} } = owner;
    const { sources = [], primary = 0 } = images || {};
    const { label, href = '' } = website;
    const { isVirtualDealer } = marketExtension;
    const ecommLink = Object.keys(ecommWebsite).length > 1 && !!ecommWebsite?.href;
    const analyticsData = {
        inventoryId,
        ownerId,
    };

    const showDealerRating = isDealerRatingEnabled && !!rating;

    /**
     * Renders dealer ratings section with conditional display
     */
    const renderDealerRating = (additionalClasses = '') => showDealerRating && (
        <KBBDealerRatings
            owner={owner}
            widgetType="inline"
            showCount
            enableImpression={false}
            className={additionalClasses}
        />
    );

    /**
     * Renders dealer license information
     */
    const renderLicense = () => !!license && (
        <DisclaimerText className="margin-top-1">
            Dealer License:
            {license}
        </DisclaimerText>
    );

    /**
     * Renders dealer address with conditional display for virtual dealers
     * @param {string} additionalClasses - Additional CSS classes for styling
     */
    const renderAddress = (additionalClasses = '') => !isVirtualDealer && !!location && (
        <div className={clsx({ 'margin-top-4': showDealerRating }, { 'margin-top-2': !showDealerRating }, 'text-size-300', additionalClasses)}>
            <OwnerAddress
                location={location}
                analyticsData={analyticsData}
            />
        </div>
    );

    /**
     * Renders phone number with conditional display and click handling
     * @param {string} additionalClasses - Additional CSS classes for styling
     */
    const renderPhoneNumber = (additionalClasses = '') => isCallEnabled && !!phone && !disableContactCTAs && (
        <div className={clsx('margin-top-4 text-size-300', additionalClasses)}>
            <OwnerPhoneNumber
                phone={phone}
                isCallable={isLessThanMd}
                phoneClickHandler={phoneClickHandler}
                analyticsData={analyticsData}
            />
        </div>
    );

    /**
     * Renders operating hours and language support
     */
    const renderHoursAndLanguages = (additionalClasses = '') => (
        <>
            {!!hours && (
                <div className={clsx('margin-top-4', additionalClasses)}>
                    <OperatingHours
                        hours={hours}
                        isDealerOpen={isDealerOpen}
                        isInline={uiContext !== 'dealerCard'}
                    />
                </div>
            )}
            {!!languages && (
                <Text
                    data-cmp="ownerLanguages"
                    componentClass="div"
                    color="subdued"
                    className={clsx({ 'margin-top-2': !!hours, 'margin-top-3': !hours })}
                >
                    {languages?.join(', ')}
                    {' '}
                    speaking assistance available
                </Text>
            )}
        </>
    );

    /**
     * Renders website CTA with conditional display
     * @param {string} uiContextValue - UI context for the CTA styling
     * @param {string} additionalClasses - Additional CSS classes for styling
     */
    const renderWebsiteCTA = (uiContextValue = 'block', additionalClasses = '') => !!href && !disableContactCTAs && (
        <div className={additionalClasses}>
            <OwnerWebsiteCTA
                className={clsx({ 'margin-top-4': isXs })}
                href={href}
                uiContext={uiContextValue}
                label={label}
                analyticsData={{
                    ctaLocation: 'dealer-detail',
                    label,
                    ecommLink,
                    ...analyticsData,
                }}
                bsStyle="primary"
            />
        </div>
    );

    if (uiContext === 'dealerCard') {
        return (
            <div
                data-cmp="ownerDetails"
                className="h-100"
            >
                <Card
                    className="iris:justify-items-center iris:items-center"
                    variant="standard"
                >
                    <Card.Content className="h-100 display-flex align-items-center flex-column">
                        <Title
                            as="h2"
                            size={isXs ? 'xxl' : 'xl'}
                            className="iris:mb-2 iris:text-center iris:text-balance"
                        >
                            {owner?.name}
                        </Title>
                        {renderDealerRating('margin-bottom-2')}
                        {renderLicense()}
                        {renderAddress('text-center iris:text-balance margin-top-auto margin-bottom-2')}
                        {renderPhoneNumber('text-center margin-top-auto margin-bottom-2')}
                        {renderHoursAndLanguages('margin-top-auto margin-bottom-2')}
                        <div className={clsx('owner-ctas-wrapper w-100 margin-top-auto')}>
                            {renderWebsiteCTA('block', 'padding-top-5')}
                        </div>
                    </Card.Content>
                </Card>
            </div>
        );
    }

    return (
        <div
            className="row padding-bottom-5"
            data-cmp="ownerDetails"
        >
            <div className="col-xs-12 col-sm-4 col-sm-push-8 col-md-3 col-md-push-9">
                <DealerImage
                    source={sources[primary]}
                    device={device}
                />
            </div>
            <div className="col-xs-12 col-sm-8 col-sm-pull-4 col-md-9 col-md-pull-3">
                <Section
                    alignment="left"
                    heading={getOwnerTitle(isDdpLinkEnabled, owner, ddpBasePath)}
                    headingComponentClass="h2"
                    uiContext="level-3"
                >
                    {renderDealerRating()}
                    {renderLicense()}
                    {renderAddress()}
                    {renderPhoneNumber()}
                    {renderHoursAndLanguages()}
                </Section>
            </div>

            <div className="col-xs-12">
                <hr className="margin-vertical-4" />
            </div>
            <div className="owner-ctas-wrapper w-100">
                {renderWebsiteCTA('block', 'col-xs-12 col-sm-4 col-md-3 owner-website-cta')}
                <div className="col-xs-12 col-sm-8 col-md-9 col-lg-8 owner-contact">
                    <OwnerContact
                        analyticsData={analyticsData}
                        disableContactCTAs={disableContactCTAs}
                        phoneClickHandler={phoneClickHandler}
                        {...rest}
                    />
                </div>
            </div>
        </div>
    );
});

OwnerDetails.propTypes = {
    /**
     * Boolean to disable phone/chat/email for the Hyundai experience
     */
    disableContactCTAs: PropTypes.bool,
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone is called replacing fireTelmetricsTag
     */
    phoneClickHandler: PropTypes.func,
    /**
     * The callback function fires when text CTA is clicked replacing fireTelmetricsTag
     */
    textClickHandler: PropTypes.func,
};

export default OwnerDetails;
